import { ChevronLeft, Clear, SearchOutlined } from 'components/icons';
import { Drawer, DrawerContent } from 'components/ui/drawer';
import { ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSearchKeyDown } from './searchInput';

export interface GlobalSearchSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The listbox's id, which this sheet's own field points `aria-controls` at. */
    listboxId: string;
    /** The option the selection is on, owned by whoever renders the search surfaces. */
    activeOptionId?: string;
    /** Exactly what is in the field. The list is built from it beside this sheet, not inside it. */
    query: string;
    onQueryChange: (query: string) => void;
    /** The list, already built for this width. */
    panel?: ReactNode;
    /** Return `true` when the list consumed the key; see the header box, which is wired the same. */
    onArrowDown?: () => boolean;
    onArrowUp?: () => boolean;
    onEnter?: () => boolean;
}

/**
 * The search surface below the breakpoint: a sheet over the whole viewport rather than a panel
 * hanging off the header's box, or the new design's ⌘K dialog.
 *
 * There is not room under the box at this width for a list worth reading, and a field at the top of
 * the screen with the keyboard over the bottom half is what the platform does anyway. So the box
 * keeps its place in the header as a button: pressing it raises this, which carries a field of its
 * own and hands the focus to it. On the way out the focus goes back to that button, which is why
 * the drawer's own default is left alone here. The new design has no box in its header: its ⌘K
 * dialog raises this instead of itself at this width.
 *
 * The list inside is the same component the dropdown draws, at the sheet's width — the rows,
 * sections, skeleton and best-bet row are not redrawn here, and neither the query nor the list is
 * this sheet's own: both belong to what raised it, the header's search or the dialog, so crossing
 * the breakpoint changes only the frame.
 */
export function GlobalSearchSheet({
    open,
    onOpenChange,
    listboxId,
    activeOptionId,
    query,
    onQueryChange,
    panel,
    onArrowDown,
    onArrowUp,
    onEnter,
}: GlobalSearchSheetProps) {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);

    const close = () => onOpenChange(false);
    const handleKeyDown = useSearchKeyDown({ query, close, surface: 'sheet', onArrowDown, onArrowUp, onEnter });
    const placeholder = intl.formatMessage({ id: 'global-search.placeholder' });

    return (
        <Drawer open={open} onOpenChange={onOpenChange} swipeDirection="right">
            <DrawerContent
                aria-label={placeholder}
                // The field, not the chevron, which is what the box being tapped was asking for.
                initialFocus={inputRef}
                /*
                 * The kit's side drawer is a 75%-wide panel with a rounded, bordered inner edge;
                 * this one is the whole viewport, so there is no inner edge to draw. The overrides
                 * carry the popup's own variants so `cn` resolves them instead of stacking them.
                 */
                className="bg-paper w-full data-[swipe-direction=right]:rounded-none data-[swipe-direction=right]:border-0"
            >
                <div className="border-divider flex flex-none items-center gap-1 border-b p-2">
                    <button
                        type="button"
                        onClick={close}
                        aria-label={intl.formatMessage({ id: 'global-search.back' })}
                        className="text-grey-600 hover:bg-grey-100 flex size-11 flex-none items-center justify-center rounded-[10px]"
                    >
                        <ChevronLeft aria-hidden className="size-5" />
                    </button>
                    <div className="border-primary-focus shadow-focus-ring flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-[10px] border pl-3">
                        <SearchOutlined aria-hidden className="text-primary size-[15px] flex-none" />
                        <input
                            ref={inputRef}
                            type="text"
                            role="combobox"
                            aria-expanded
                            aria-controls={listboxId}
                            aria-activedescendant={activeOptionId}
                            aria-autocomplete="list"
                            aria-label={placeholder}
                            placeholder={placeholder}
                            autoComplete="off"
                            spellCheck={false}
                            value={query}
                            onChange={(event) => onQueryChange(event.target.value)}
                            onKeyDown={handleKeyDown}
                            className="text-grey-700 placeholder:text-grey-400 new:placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none"
                        />
                        {query !== '' && (
                            <button
                                type="button"
                                aria-label={intl.formatMessage({ id: 'clear' })}
                                onClick={() => {
                                    onQueryChange('');
                                    inputRef.current?.focus();
                                }}
                                className="text-grey-500 hover:text-grey-700 flex size-11 flex-none items-center justify-center"
                            >
                                <Clear aria-hidden className="size-4" />
                            </button>
                        )}
                    </div>
                </div>
                {panel}
            </DrawerContent>
        </Drawer>
    );
}
